import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import { useTexts, useTranslation } from '@/feature/lang';
import { Icon } from '@/icon/Icon';

const InputContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(0),
  padding: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const StyledTextArea = styled('textarea')(({ theme }) => ({
  borderRadius: 18,
  height: '36px',
  maxHeight: '96px',
  minHeight: '36px',
  padding: theme.spacing(1, 1.5),
  lineHeight: '1.5',
  border: 'none',
  outline: 'none',
  resize: 'none',
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  backgroundColor: theme.palette.getGrey(20),
  color: theme.palette.text.primary,
  overflow: 'auto',
  width: '100%',
  '::placeholder': { color: theme.palette.text.disabled },
}));

interface GptChatInputProps {
  onSendMessage?: (message: string) => void;
  disabled?: boolean;
}

export const ChatInput = ({
  onSendMessage,
  disabled = false,
}: GptChatInputProps) => {
  const texts = useTexts().component.chatbot.input;
  const { translate } = useTranslation();

  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    if (message.trim() && onSendMessage && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '36px';
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);

    const textarea = event.target;
    textarea.style.height = '36px';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 96)}px`;
  };

  return (
    <InputContainer>
      <StyledTextArea
        ref={textareaRef}
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={translate(texts.placeholder)}
        rows={1}
        disabled={disabled}
      />
      <IconButton
        onClick={handleSend}
        disabled={!message.trim() || disabled}
        color="primary"
        size="small"
        sx={{ marginBottom: '1px' }}
      >
        <Icon name="send" />
      </IconButton>
    </InputContainer>
  );
};
